import { apiService } from './api';
import { profileCache, searchCache, requestDeduplicator, LRUCache } from '../utils/cache';

// Cached API wrapper for profile-related endpoints
class CachedApiService {
    constructor() {
        this.apiService = apiService;
        
        // Abort controllers for cancellable requests
        this.abortControllers = new Map();
    }

    // Search profiles with caching and deduplication
    async searchProfiles(params = {}) {
        const cacheKey = LRUCache.generateKey({ ...params, endpoint: 'searchProfiles' });
        
        // Check cache first
        const cached = searchCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        // Deduplicate concurrent requests
        const response = await requestDeduplicator.deduplicate(cacheKey, async () => {
            // Create abort controller for this request
            const abortController = new AbortController();
            const requestId = `search-${Date.now()}`;
            this.abortControllers.set(requestId, abortController);

            try {
                const result = await this.apiService.searchProfiles(params);
                
                // Cache successful responses
                if (result.success) {
                    searchCache.set(cacheKey, result);
                }
                
                return result;
            } finally {
                // Clean up abort controller
                this.abortControllers.delete(requestId);
            }
        });

        return response;
    }

    // Get single profile with caching
    async getProfile(id) {
        const cacheKey = `profile-${id}`;
        
        // Check cache first
        const cached = profileCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        // Deduplicate concurrent requests
        const response = await requestDeduplicator.deduplicate(cacheKey, async () => {
            try {
                const result = await this.apiService.getProfile(id);
                
                // Cache successful responses and 404s (profile not found)
                if (result.success) {
                    profileCache.set(cacheKey, result);
                    
                    // Also update the profile in search results if present
                    this.updateProfileInSearchCache(id, result.data);
                } else if (result.status === 404) {
                    profileCache.set(cacheKey, result);
                }
                
                return result;
            } catch (error) {
                // Handle network errors vs 404s
                if (error.response?.status === 404) {
                    const notFoundResult = {
                        success: false,
                        status: 404,
                        message: 'Profile not found',
                        data: null
                    };
                    profileCache.set(cacheKey, notFoundResult);
                    return notFoundResult;
                }
                throw error;
            }
        });

        return response;
    }

    // Get profile by phone with caching
    async getProfileByPhone(phone, channel = null) {
        const cacheKey = `profile-phone-${channel || 'default'}-${phone}`;
        
        const cached = profileCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const response = await requestDeduplicator.deduplicate(cacheKey, async () => {
            try {
                const result = await this.apiService.getProfileByPhone(phone, channel);
                
                // Cache successful responses and 404s (profile not found)
                if (result.success || result.status === 404) {
                    profileCache.set(cacheKey, result);
                }
                
                return result;
            } catch (error) {
                // Handle network errors vs 404s
                if (error.response?.status === 404) {
                    const notFoundResult = {
                        success: false,
                        status: 404,
                        message: 'Profile not found',
                        data: null
                    };
                    profileCache.set(cacheKey, notFoundResult);
                    return notFoundResult;
                }
                throw error;
            }
        });

        return response;
    }

    // Profile analytics with caching
    async getProfileAnalytics(id = null) {
        const cacheKey = id ? `analytics-${id}` : 'analytics-global';
        
        const cached = searchCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const response = await this.apiService.getProfileAnalytics(id);
        
        if (response.success) {
            searchCache.set(cacheKey, response);
        }
        
        return response;
    }

    // Profile stats with caching
    async getProfileStats() {
        const cacheKey = 'profile-stats';
        
        const cached = searchCache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const response = await this.apiService.getProfileStats();
        
        if (response.success) {
            searchCache.set(cacheKey, response);
        }
        
        return response;
    }

    // Create profile (invalidates cache)
    async createProfile(profileData) {
        const response = await this.apiService.createProfile(profileData);
        
        if (response.success) {
            // Invalidate relevant caches
            this.invalidateSearchCache();
            this.invalidateStatsCache();
        }
        
        return response;
    }

    // Update profile (invalidates cache)
    async updateProfile(id, updateData) {
        const response = await this.apiService.updateProfile(id, updateData);
        
        if (response.success) {
            // Remove from cache
            profileCache.clear(); // Clear all profile cache as the update might affect search results
            this.invalidateSearchCache();
            this.invalidateStatsCache();
            
            // Update in search cache if present
            this.updateProfileInSearchCache(id, response.data);
        }
        
        return response;
    }

    // Link/unlink profiles (invalidates cache - linkedProfiles rides along on
    // every profile lookup, so a stale entry would show the wrong links)
    async linkProfile(profileId, otherProfileId) {
        const response = await this.apiService.linkProfile(profileId, otherProfileId);

        if (response.success) {
            profileCache.clear();
            this.invalidateSearchCache();
        }

        return response;
    }

    async unlinkProfile(profileId, linkedProfileId) {
        const response = await this.apiService.unlinkProfile(profileId, linkedProfileId);

        if (response.success) {
            profileCache.clear();
            this.invalidateSearchCache();
        }

        return response;
    }

    // Delete profile (invalidates cache)
    async deleteProfile(id) {
        const response = await this.apiService.deleteProfile(id);
        
        if (response.success) {
            // Invalidate caches
            profileCache.clear();
            this.invalidateSearchCache();
            this.invalidateStatsCache();
        }
        
        return response;
    }

    // Drop cached profile lookups. Called when a contact's details change
    // server side - a cached pre-enrichment answer would otherwise stick for
    // the rest of the session.
    invalidateProfiles() {
        profileCache.clear();
    }

    // Cancel all pending search requests
    cancelPendingSearches() {
        this.abortControllers.forEach((controller, id) => {
            if (id.startsWith('search-')) {
                controller.abort();
            }
        });
    }

    // Helper: Update profile in search cache results
    updateProfileInSearchCache(profileId, updatedProfile) {
        // Iterate through search cache and update the profile if found
        for (const [key, item] of searchCache.cache.entries()) {
            if (item.value?.data && Array.isArray(item.value.data)) {
                const index = item.value.data.findIndex(p => p._id === profileId);
                if (index !== -1) {
                    item.value.data[index] = updatedProfile;
                }
            }
        }
    }

    // Helper: Invalidate search-related caches
    invalidateSearchCache() {
        // Clear all search cache entries
        const keysToDelete = [];
        for (const key of searchCache.cache.keys()) {
            try {
                const parsed = JSON.parse(key);
                if (parsed && parsed.endpoint === 'searchProfiles') {
                    keysToDelete.push(key);
                }
            } catch (e) {
                // Skip non-JSON keys (they're not search-related)
                continue;
            }
        }
        keysToDelete.forEach(key => searchCache.cache.delete(key));
    }

    // Helper: Invalidate stats cache
    invalidateStatsCache() {
        searchCache.cache.delete('"profile-stats"');
        searchCache.cache.delete('"analytics-global"');
    }

    // Clear all caches
    clearAllCaches() {
        profileCache.clear();
        searchCache.clear();
        requestDeduplicator.clear();
    }

    // Get cache statistics
    getCacheStats() {
        return {
            profileCache: {
                size: profileCache.size(),
                maxSize: profileCache.maxSize
            },
            searchCache: {
                size: searchCache.size(),
                maxSize: searchCache.maxSize
            },
            pendingRequests: requestDeduplicator.pending.size
        };
    }
}

// Create singleton instance
export const cachedApiService = new CachedApiService();

// Export for direct access if needed
export default cachedApiService;